/**
 * @author      Michael Hettmer <mail@michael-hettmer.de>
 * @copyright   2019 Plusbyte UG (haftungsbeschränkt)
 * @license     {@link https://plusbyte.de Plusbyte License}
 */

import React, { memo } from 'react';
import { useSelector } from 'react-redux';

import {
  selectCurrentHorse,
  selectCurrentState,
  selectHorses,
  selectOwnHorses,
  selectRound,
  selectTurnDicesPlayerId,
} from '~/app';
import HorseIcon from '~/components/HorseIcon';
import Turfmaster from '~/server/src/tmmodel';
import { cn } from '~/utils/styles';

export interface HorseSelectionProps {
  selectedOwnHorse: number;
  onOwnHorseSelected: (ownHorse: number) => void;
  /** When true, interactions are disabled (viewing in-game history). */
  isHistoryMode?: boolean;
  /** When false, horse selection clicks are ignored (e.g. during dice-selection phases). Default true. */
  canSwitchViewedHorse?: boolean;
}

const HorseSelection = ({
  onOwnHorseSelected,
  selectedOwnHorse,
  isHistoryMode = false,
  canSwitchViewedHorse = true,
}: HorseSelectionProps) => {
  const currentRound = useSelector(selectRound);
  const currentState = useSelector(selectCurrentState);
  const turnDicesPlayerId = useSelector(selectTurnDicesPlayerId);
  const horses = useSelector(selectHorses);
  const ownHorses = useSelector(selectOwnHorses);
  const currentHorse = useSelector(selectCurrentHorse);

  const currentHorseIndex = currentHorse ? currentHorse.index : -1;
  const turnDicesPlayerHorsesId = horses
    .filter((horse) => horse.playerId === turnDicesPlayerId)
    .map((horse) => horse.index);

  return (
    <div className="flex h-full w-full flex-col px-4 py-6">
      <div className="grid grow auto-rows-[minmax(3rem,1fr)] grid-cols-[repeat(auto-fit,minmax(3rem,1fr))] gap-2">
        {ownHorses.map((horse, index) => {
          const selected =
            // prevent horse from being selected while last turn in round is shown with a delay
            currentState !== Turfmaster.stateMachine.SELECTMOVEOPP &&
            // prevent horse being selected before first and after last round
            currentRound >= 0 &&
            currentRound < Turfmaster.rounds &&
            currentState === Turfmaster.stateMachine.SELECTDICES
              ? turnDicesPlayerHorsesId.includes(horse.index)
              : horse.index === currentHorseIndex;
          return (
            <HorseIcon
              key={horse.index}
              className={cn('opacity-50 transition-all duration-150 active:scale-[0.9]', {
                'opacity-100': selectedOwnHorse === index,
                'cursor-pointer': !isHistoryMode && canSwitchViewedHorse,
                'pointer-events-none cursor-not-allowed': isHistoryMode || !canSwitchViewedHorse,
              })}
              selected={selected}
              color={horse.color}
              onClick={() => !isHistoryMode && canSwitchViewedHorse && onOwnHorseSelected(index)}
            />
          );
        })}
      </div>
    </div>
  );
};

export default memo(HorseSelection);
